import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Spin, Button, Flex, Row, Col, Grid } from "antd";
import {
    ArrowLeftOutlined, ShoppingCartOutlined, MinusOutlined, PlusOutlined,
    CloseOutlined, CarOutlined, ExclamationCircleOutlined, RightOutlined,
} from "@ant-design/icons";
import { User } from "../models/User";
import { OrderItem } from "../models/OrderItem";
import { Restaurant } from "../models/Restaurant";
import { OrderService } from "../services/OrderService";
import { PaymentService } from "../services/PaymentService";
import { AuthenticationService } from "../services/AuthenticationService";
import TopNavigation, { HEADER_HEIGHT } from "./snipets/TopNavigation";
import Footer from "./snipets/Footer";
import DriverSelectionModal from "../modals/DriverSelectionModal";
import {SOCKET_URL} from "../Utils/axiosInterceptor";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const BRAND         = "#00AF66";
const BRAND_LIGHT   = "#E6F7EF";
const DARK          = "#1A1A2E";
const MUTED         = "#8F9BB3";
const ERROR         = "#E53935";
const ERROR_LIGHT   = "#FFEBEE";
const SERVICE_FEE   = 10;
const STANDARD_RATE = 40;

// Currency values must never wrap mid-number — pair with a flexible sibling
// (flex:1, minWidth:0) so the label yields space instead of the price.
const priceStyle: React.CSSProperties = { whiteSpace: "nowrap", flexShrink: 0 };
const labelFlexStyle: React.CSSProperties = { flex: 1, minWidth: 0 };

const CartPage: React.FC = () => {
    const navigate = useNavigate();
    const screens = useBreakpoint();

    const [cartItems,           setCartItems]           = useState<OrderItem[]>([]);
    const [loadingCart,         setLoadingCart]         = useState(true);
    const [driverModalVisible,  setDriverModalVisible]  = useState(false);
    const [selectedDriver,      setSelectedDriver]      = useState<User | null>(null);
    const [driverError,         setDriverError]         = useState(false);
    const [availableDrivers,    setAvailableDrivers]    = useState<User[]>([]);
    const [loadingDrivers,      setLoadingDrivers]      = useState(false);
    const [noDriversFound,      setNoDriversFound]      = useState(false);
    const [loading,             setLoading]             = useState(false);
    const [loadingDistance,     setLoadingDistance]     = useState(false);
    const [currentUser,         setCurrentUser]         = useState<User>(new User());
    const [selectedRestaurants, setSelectedRestaurants] = useState<Restaurant[]>([]);
    const [distanceKm,          setDistanceKm]          = useState<number>(0);

    // ── Group duplicate items for display ─────────────────────────────────────
    const groupedItems = cartItems.reduce((acc, item) => {
        const existing = acc.find(i => i.menu_item.id === item.menu_item.id);
        if (existing) {
            existing.quantity += item.quantity;
        } else {
            const copy = new OrderItem();
            Object.assign(copy, item);
            acc.push(copy);
        }
        return acc;
    }, [] as OrderItem[]);

    // ── Price calculation — identical to the mobile version ─────────────────
    const subtotal = groupedItems.reduce((sum, item) => {
        const unitPrice = item.flavour?.price ?? item.menu_item?.flavours?.[0]?.price ?? 0;
        return sum + (unitPrice / 100) * item.quantity;
    }, 0);

    const totalItems      = groupedItems.reduce((sum, item) => sum + item.quantity, 0);
    const restaurantCount = selectedRestaurants.length;
    const multiShopFee    = restaurantCount > 1 ? SERVICE_FEE * (restaurantCount - 1) * 2 : 0;
    const isStandardRate  = selectedDriver?.id === -1;
    const ratePerBlock    = isStandardRate ? STANDARD_RATE : (selectedDriver?.price ?? 0);
    const driverFee       = selectedDriver ? Math.round((distanceKm / 5) * ratePerBlock * 100) / 100 : 0;
    const total            = Math.round((subtotal + SERVICE_FEE + multiShopFee + driverFee) * 100) / 100;

    const fetchDistance = async () => {
        setLoadingDistance(true);
        try {
            const data = await OrderService.getOrderDistance();
            setDistanceKm(data.distance_km);
        } catch (e) {
            setDistanceKm(0);
        } finally {
            setLoadingDistance(false);
        }
    };

    // ── Cart actions ──────────────────────────────────────────────────────────
    const updateQuantity = (id: number, delta: number) => {
        setCartItems(prev =>
            prev
                .map(item => {
                    if (item.id !== id) return item;
                    const updated = new OrderItem();
                    Object.assign(updated, item);
                    updated.quantity = item.quantity + delta;
                    OrderService.updateOrderItemQuantityByOrderItemId(id, updated.quantity);
                    return updated;
                })
                .filter(item => item.quantity > 0)
        );
    };

    const removeItem = (id: number) => {
        setCartItems(prev => prev.filter(item => item.id !== id));
        OrderService.removeOrderItemById(id).then(() => {
            OrderService.getOrderRestaurantsByUser().then((restaurants: Restaurant[]) => {
                setSelectedRestaurants(restaurants);
            });
        });
    };

    // ── Checkout ──────────────────────────────────────────────────────────────
    const handleCheckout = async () => {
        if (!selectedDriver) {
            setDriverError(true);
            return;
        }

        setLoading(true);

        try {
            // Send the driver's id, not their price — same server-side safeguard
            // as mobile: the backend resolves the real rate from this id, so a
            // stale/tampered client-side price can never determine the charge.
            const idToSend = isStandardRate ? 0 : selectedDriver.id;
            const response = await PaymentService.retrievePaymentPaymentLink(idToSend);

            const authorization_url = response.url;
            const order_ref         = response.order_ref;

            await new Promise((resolve, reject) => {
                const socket = new WebSocket(SOCKET_URL + `payment/${currentUser.id}/`);

                socket.onopen = async () => {
                    await OrderService.setOrderType(isStandardRate ? null : selectedDriver);
                    // New tab, not a full redirect — keeps this tab (and the
                    // socket waiting for payment confirmation) alive, mirroring
                    // the mobile app staying resident while an external browser opens.
                    window.open(authorization_url, "_blank");
                };

                socket.onmessage = (event) => {
                    const data = JSON.parse(event.data);
                    if (data.order_ref === order_ref) {
                        socket.close();
                        if (data.status === "success") {
                            resolve(data);
                            OrderService.confirmOrder(selectedDriver).then(() => {});
                            navigate("/orderTracking");
                        } else {
                            reject(new Error(data.status));
                        }
                        // NOTE: mobile sends a local push notification here.
                        // No direct web equivalent wired up yet (would need the
                        // Notifications API + a permission prompt) — intentionally
                        // left out rather than faked.
                    }
                };

                socket.onerror = () => reject(new Error("WebSocket error"));

                setTimeout(() => {
                    socket.close();
                    reject(new Error("Payment timeout"));
                }, 300000);
            });

            navigate("/orderTracking");

        } catch (error) {
            setLoading(false);
        }
    };

    // ── Driver modal ──────────────────────────────────────────────────────────
    const handleOpenDriverModal = () => {
        setDriverError(false);
        setNoDriversFound(false);
        setLoadingDrivers(true);
        setAvailableDrivers([]);
        setDriverModalVisible(true);

        if (!("geolocation" in navigator)) {
            setLoadingDrivers(false);
            setDriverError(true);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    const drivers: User[] = await OrderService.getAvailableDrivers(
                        pos.coords.latitude,
                        pos.coords.longitude
                    );
                    setAvailableDrivers(drivers);
                    if (drivers.length === 0) setNoDriversFound(true);
                } catch (e) {
                    setDriverError(true);
                } finally {
                    setLoadingDrivers(false);
                }
            },
            () => {
                setLoadingDrivers(false);
                setDriverError(true);
            }
        );
    };

    const handleUseStandardRate = () => {
        const standardDriver   = new User();
        standardDriver.id      = -1;
        standardDriver.name    = "Standard";
        standardDriver.surname = "Rate";
        standardDriver.price   = STANDARD_RATE;
        setSelectedDriver(standardDriver);
        setDriverError(false);
        setDriverModalVisible(false);
    };

    // ── Load data ─────────────────────────────────────────────────────────────
    const loadCart = () => {
        setLoadingCart(true);
        OrderService.getActiveOrdersByUser().then((orders: OrderItem[]) => {
            if (orders.length > 0) {
                navigate("/orderTracking");
                return;
            }
        });

        Promise.all([
            OrderService.readCartByUser(),
            OrderService.getOrderRestaurantsByUser(),
            AuthenticationService.getUserProfile(),
            fetchDistance(),
        ]).then(([items, restaurants, user]) => {
            setCartItems(items);
            setSelectedRestaurants(restaurants);
            setCurrentUser(user);
        }).finally(() => {
            setLoadingCart(false);
        });
    };

    useEffect(() => {
        loadCart();

        const handleVisibility = () => {
            if (document.visibilityState === "visible") {
                OrderService.getActiveOrdersByUser().then((orders: OrderItem[]) => {
                    if (orders.length > 0) {
                        navigate("/orderTracking");
                        return;
                    }
                });
                OrderService.readCartByUser().then(setCartItems);
                OrderService.getOrderRestaurantsByUser().then(setSelectedRestaurants);
                fetchDistance().then();
            }
        };

        document.addEventListener("visibilitychange", handleVisibility);
        return () => document.removeEventListener("visibilitychange", handleVisibility);
    }, []);

    const showFloatingBar = !screens.lg && !loadingCart && groupedItems.length > 0;

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <>
            <TopNavigation />

            <div style={{ paddingTop: HEADER_HEIGHT, background: "#F7F9FC", minHeight: "100dvh" }}>
                <Flex align="center" gap={12} style={{ padding: "16px clamp(16px, 4vw, 24px)", background: "#fff", borderBottom: "1px solid #EDF1F7" }}>
                    <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />
                    <Title level={4} style={{ margin: 0 }}>Your cart</Title>
                </Flex>

                {loadingCart ? (
                    <Flex vertical align="center" gap={14} style={{ padding: "80px 0" }}>
                        <Spin size="large" />
                        <Text type="secondary">Loading your cart…</Text>
                    </Flex>
                ) : (
                    <div style={{
                        padding: `24px clamp(16px, 4vw, 24px) ${showFloatingBar ? 130 : 40}px`,
                        maxWidth: 1100,
                        margin: "0 auto",
                    }}>
                        {groupedItems.length === 0 ? (
                            <Flex vertical align="center" gap={8} style={{ background: "#fff", borderRadius: 12, padding: 48, marginTop: 24 }}>
                                <ShoppingCartOutlined style={{ fontSize: 32, color: BRAND }} />
                                <Text strong style={{ fontSize: 15 }}>Your cart is empty</Text>
                                <Text type="secondary" style={{ textAlign: "center" }}>
                                    Browse a restaurant and add something delicious to get started!
                                </Text>
                            </Flex>
                        ) : (
                            <Row gutter={[24, 24]}>
                                <Col xs={24} lg={15}>
                                    <Flex vertical gap={14}>
                                        {groupedItems.map(item => {
                                            const unitPrice        = item.flavour?.price ?? item.menu_item?.flavours?.[0]?.price ?? 0;
                                            const unitPriceDisplay = (unitPrice / 100).toFixed(2);
                                            const lineTotalDisplay = ((unitPrice / 100) * item.quantity).toFixed(2);

                                            return (
                                                <div key={item.id} style={{ background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
                                                    <Flex align="center" justify="space-between" gap={8} style={{ padding: 16 }}>
                                                        <Text strong style={{ fontSize: 17, ...labelFlexStyle }}>{item.menu_item.name}</Text>
                                                        <Button
                                                            type="text"
                                                            icon={<CloseOutlined style={{ color: MUTED }} />}
                                                            onClick={() => removeItem(item.id)}
                                                            style={{ flexShrink: 0 }}
                                                        />
                                                    </Flex>
                                                    <div style={{ height: 1, background: "#EDF1F7" }} />

                                                    {item.flavour && item.flavour.name && (
                                                        <Flex justify="space-between" align="center" gap={12} style={{ padding: "10px 16px" }}>
                                                            <Text style={{ color: "#4A4A6A", ...labelFlexStyle }}>{item.flavour.name}</Text>
                                                            {item.flavour.description && (
                                                                <Text strong style={{ flexShrink: 0 }}>{item.flavour.description}</Text>
                                                            )}
                                                        </Flex>
                                                    )}

                                                    {item.menu_item.description && (
                                                        <Flex justify="space-between" align="center" gap={12} style={{ padding: "10px 16px" }}>
                                                            <Text style={{ color: "#4A4A6A", ...labelFlexStyle }}>{item.menu_item.description}</Text>
                                                            <Text strong style={priceStyle}>R {unitPriceDisplay}</Text>
                                                        </Flex>
                                                    )}

                                                    <div style={{ height: 1, background: "#EDF1F7" }} />

                                                    <Flex align="center" gap={14} style={{ padding: 16 }}>
                                                        <Button
                                                            shape="circle"
                                                            icon={<MinusOutlined style={{ color: BRAND }} />}
                                                            onClick={() => updateQuantity(item.id, -1)}
                                                        />
                                                        <Text strong style={{ minWidth: 20, textAlign: "center" }}>{item.quantity}</Text>
                                                        <Button
                                                            shape="circle"
                                                            icon={<PlusOutlined style={{ color: BRAND }} />}
                                                            onClick={() => updateQuantity(item.id, 1)}
                                                        />
                                                        <Text type="secondary" style={{ fontSize: 12, ...priceStyle }}>R {unitPriceDisplay} each</Text>
                                                        <div style={{ flex: 1, minWidth: 8 }} />
                                                        <Text strong style={{ fontSize: 16, ...priceStyle }}>R {lineTotalDisplay}</Text>
                                                    </Flex>
                                                </div>
                                            );
                                        })}

                                        <Flex
                                            align="center"
                                            justify="space-between"
                                            gap={12}
                                            onClick={handleOpenDriverModal}
                                            style={{
                                                background: driverError ? ERROR_LIGHT : selectedDriver ? BRAND_LIGHT : "#fff",
                                                border: `1.5px ${selectedDriver || driverError ? "solid" : "dashed"} ${driverError ? ERROR : selectedDriver ? BRAND : "#EDF1F7"}`,
                                                borderRadius: 12,
                                                padding: 16,
                                                cursor: "pointer",
                                            }}
                                        >
                                            <Flex align="center" gap={12} style={labelFlexStyle}>
                                                {driverError
                                                    ? <ExclamationCircleOutlined style={{ fontSize: 22, color: ERROR, flexShrink: 0 }} />
                                                    : <CarOutlined style={{ fontSize: 22, color: selectedDriver ? BRAND : MUTED, flexShrink: 0 }} />}
                                                <div style={{ minWidth: 0 }}>
                                                    <Text strong style={{ color: driverError ? ERROR : DARK, display: "block" }}>
                                                        {selectedDriver
                                                            ? isStandardRate ? "Standard Rate (R40/5km)" : `${selectedDriver.name} ${selectedDriver.surname}`
                                                            : "Select a Driver"}
                                                    </Text>
                                                    <Text type="secondary" style={{ fontSize: 12, color: driverError ? ERROR : MUTED }}>
                                                        {driverError
                                                            ? "Please select a driver to continue"
                                                            : selectedDriver
                                                                ? isStandardRate ? "Tap to retry for nearby drivers" : `R${selectedDriver.price}/5km · Tap to change`
                                                                : "Choose who delivers your order"}
                                                    </Text>
                                                </div>
                                            </Flex>
                                            <RightOutlined style={{ color: driverError ? ERROR : MUTED, flexShrink: 0 }} />
                                        </Flex>
                                    </Flex>
                                </Col>

                                <Col xs={24} lg={9}>
                                    <div style={{
                                        background: "#fff", borderRadius: 12, padding: 20,
                                        boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                                        position: screens.lg ? "sticky" : "static",
                                        top: HEADER_HEIGHT + 24,
                                    }}>
                                        <Title level={5} style={{ marginBottom: 14 }}>Order Summary</Title>

                                        <Flex justify="space-between" gap={12} style={{ marginBottom: 10 }}>
                                            <Text type="secondary" style={labelFlexStyle}>Subtotal</Text>
                                            <Text strong style={priceStyle}>R {subtotal.toFixed(2)}</Text>
                                        </Flex>

                                        <Flex justify="space-between" gap={12} style={{ marginBottom: 10 }}>
                                            <Text type="secondary" style={labelFlexStyle}>Service Fee</Text>
                                            <Text strong style={priceStyle}>R {SERVICE_FEE.toFixed(2)}</Text>
                                        </Flex>

                                        {restaurantCount > 1 && (
                                            <Flex justify="space-between" gap={12} style={{ marginBottom: 10 }}>
                                                <Text type="secondary" style={labelFlexStyle}>Multiple Restaurant Fee</Text>
                                                <Text strong style={priceStyle}>R {multiShopFee.toFixed(2)}</Text>
                                            </Flex>
                                        )}

                                        <Flex justify="space-between" align="center" gap={12} style={{ marginBottom: 10 }}>
                                            <Text type="secondary" style={labelFlexStyle}>Driver Fee</Text>
                                            {loadingDistance ? <Spin size="small" /> : (
                                                <Text strong style={priceStyle}>{selectedDriver ? `R ${driverFee.toFixed(2)}` : "Select a driver"}</Text>
                                            )}
                                        </Flex>

                                        <div style={{ height: 1, background: "#EDF1F7", margin: "10px 0" }} />

                                        <Flex justify="space-between" align="center" gap={12} style={{ marginBottom: 20 }}>
                                            <Text strong style={{ fontSize: 16, ...labelFlexStyle }}>Total</Text>
                                            {loadingDistance ? <Spin size="small" /> : (
                                                <Text strong style={{ fontSize: 16, color: BRAND, ...priceStyle }}>R {total.toFixed(2)}</Text>
                                            )}
                                        </Flex>

                                        <Text type="secondary" style={{ display: "block", textAlign: "center", fontSize: 12, marginBottom: 12 }}>
                                            Estimated Delivery Time: ± 35 min
                                        </Text>

                                        <Button
                                            type="primary"
                                            size="large"
                                            block
                                            loading={loading}
                                            disabled={loading || loadingDistance}
                                            onClick={handleCheckout}
                                            style={{ background: BRAND, borderColor: BRAND, height: 52 }}
                                        >
                                            {totalItems} {totalItems === 1 ? "item" : "items"} — Checkout
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </div>
                )}
            </div>

            {showFloatingBar && (
                <div style={{
                    position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 30,
                    display: "flex", justifyContent: "center",
                    padding: "0 16px 16px",
                    pointerEvents: "none",
                }}>
                    <Button
                        type="primary"
                        onClick={handleCheckout}
                        loading={loading}
                        disabled={loading || loadingDistance}
                        style={{
                            pointerEvents: "auto",
                            background: BRAND, borderColor: BRAND,
                            height: 56, borderRadius: 12, width: "100%", maxWidth: 480,
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "0 20px",
                            boxShadow: "0 6px 20px rgba(0,175,102,0.35)",
                        }}
                    >
                        <span style={{ fontWeight: 800, whiteSpace: "nowrap" }}>{totalItems} {totalItems === 1 ? "item" : "items"}</span>
                        <span style={{ fontWeight: 800, whiteSpace: "nowrap" }}>Checkout</span>
                        <span style={{ fontWeight: 700, whiteSpace: "nowrap" }}>R {total.toFixed(2)}</span>
                    </Button>
                </div>
            )}

            <DriverSelectionModal
                visible={driverModalVisible}
                loadingDrivers={loadingDrivers}
                noDriversFound={noDriversFound}
                availableDrivers={availableDrivers}
                onClose={() => setDriverModalVisible(false)}
                onSelectDriver={(driver) => {
                    setSelectedDriver(driver);
                    setDriverError(false);
                    setDriverModalVisible(false);
                }}
                onUseStandardRate={handleUseStandardRate}
                onWaitForDriver={() => setDriverModalVisible(false)}
            />

            <Footer />
        </>
    );
};

export default CartPage;